import React from 'react'
import { Card, Form, Button, ButtonGroup, Badge } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrash, faPlus, faSquare, faObjectGroup } from '@fortawesome/free-solid-svg-icons'
import PredicatePicker from './PredicatePicker'
import LeafPredicateForm from './LeafPredicateForm'
import { isComposite, isLeaf, newLeaf, newComposite, COMPOSITE_OPS } from './recipe-helpers'

const TreeNode = ({ node, registry, onChange, onRemove, depth = 0, removable = true }) => {
  if (isComposite(node)) {
    return (
      <CompositeNodeView
        node={node}
        registry={registry}
        onChange={onChange}
        onRemove={onRemove}
        depth={depth}
        removable={removable}
      />
    )
  }
  if (isLeaf(node)) {
    return (
      <LeafNodeView
        node={node}
        registry={registry}
        onChange={onChange}
        onRemove={onRemove}
        removable={removable}
      />
    )
  }
  return <Badge bg="warning">Invalid node</Badge>
}

const CompositeNodeView = ({ node, registry, onChange, onRemove, depth, removable }) => {
  const setOp = (op) => {
    // This node keeps occupying the same slot in its parent's children —
    // preserve its own `_key` across the conversion so a reorder later
    // doesn't get confused about which rendered subtree is which (the
    // wrapped child node keeps its own key automatically, since it's the
    // same object reference either way).
    if (op === 'NOT') {
      const child = node.predicates?.[0] || node.predicate || newLeaf()
      onChange({ op: 'NOT', predicate: child, _key: node._key })
    } else if (node.op === 'NOT') {
      onChange({ op, predicates: [node.predicate || newLeaf()], _key: node._key })
    } else {
      onChange({ ...node, op })
    }
  }

  const addChild = (kind) => {
    const child = kind === 'leaf' ? newLeaf() : newComposite('AND')
    if (node.op === 'NOT') {
      onChange({ op: 'NOT', predicate: child })
    } else {
      onChange({ ...node, predicates: [...(node.predicates || []), child] })
    }
  }

  const replaceChild = (idx, next) => {
    if (node.op === 'NOT') {
      onChange({ ...node, predicate: next })
    } else {
      const arr = [...(node.predicates || [])]
      arr[idx] = next
      onChange({ ...node, predicates: arr })
    }
  }

  const removeChild = (idx) => {
    if (node.op === 'NOT') {
      onChange({ ...node, predicate: newLeaf() })
    } else {
      const arr = (node.predicates || []).filter((_, i) => i !== idx)
      if (arr.length === 0) arr.push(newLeaf())
      onChange({ ...node, predicates: arr })
    }
  }

  const moveChild = (idx, direction) => {
    if (node.op === 'NOT') return
    const arr = [...(node.predicates || [])]
    const target = idx + direction
    if (target < 0 || target >= arr.length) return
    const [item] = arr.splice(idx, 1)
    arr.splice(target, 0, item)
    onChange({ ...node, predicates: arr })
  }

  const bg = depth % 2 === 0 ? 'bg-light' : 'bg-white'
  const children = node.op === 'NOT' ? (node.predicate ? [node.predicate] : []) : (node.predicates || [])

  return (
    <Card className={`mb-2 ${bg}`}>
      <Card.Body className="py-2">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <div className="d-flex align-items-center">
            <FontAwesomeIcon icon={faObjectGroup} className="text-primary me-2" />
            <ButtonGroup size="sm" className="me-2">
              {COMPOSITE_OPS.map((op) => (
                <Button
                  key={op}
                  variant={node.op === op ? 'primary' : 'light'}
                  className={node.op === op ? '' : 'text-primary'}
                  onClick={() => setOp(op)}
                >
                  {op}
                </Button>
              ))}
            </ButtonGroup>
            <Badge bg="light" text="primary">composite</Badge>
          </div>
          <div>
            {node.op !== 'NOT' && (
              <>
                <Button variant="light" className="text-primary me-1" size="sm" onClick={() => addChild('leaf')}>
                  <FontAwesomeIcon icon={faPlus} className="me-1" />
                  Predicate
                </Button>
                <Button variant="light" className="text-primary me-1" size="sm" onClick={() => addChild('group')}>
                  <FontAwesomeIcon icon={faPlus} className="me-1" />
                  Group
                </Button>
              </>
            )}
            {removable && (
              <Button variant="light" className="text-danger" size="sm" onClick={onRemove}>
                <FontAwesomeIcon icon={faTrash} />
              </Button>
            )}
          </div>
        </div>
        {children.length === 0 ? (
          <div className="text-muted small ps-3">Empty group — add a child predicate.</div>
        ) : (
          <div className="ps-3 border-start">
            {children.map((child, idx) => (
              <div key={child._key ?? idx} className="d-flex align-items-stretch">
                {node.op !== 'NOT' && children.length > 1 && (
                  <ButtonGroup vertical size="sm" className="me-2">
                    <Button variant="light" className="text-primary px-1" onClick={() => moveChild(idx, -1)} disabled={idx === 0}>↑</Button>
                    <Button variant="light" className="text-primary px-1" onClick={() => moveChild(idx, 1)} disabled={idx === children.length - 1}>↓</Button>
                  </ButtonGroup>
                )}
                <div className="flex-grow-1">
                  <TreeNode
                    node={child}
                    registry={registry}
                    depth={depth + 1}
                    onChange={(next) => replaceChild(idx, next)}
                    onRemove={() => removeChild(idx)}
                    removable={node.op !== 'NOT' || children.length > 1}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </Card.Body>
    </Card>
  )
}

const LeafNodeView = ({ node, registry, onChange, onRemove, removable }) => {
  const schema = registry?.predicates?.[node.type]
  const toGroup = () => onChange(newComposite('AND'))

  return (
    <Card className="mb-2 bg-white border-primary border-opacity-25">
      <Card.Body className="py-2">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <div className="d-flex align-items-center" style={{ width: '60%' }}>
            <FontAwesomeIcon icon={faSquare} className="text-primary me-2" />
            <PredicatePicker
              value={node.type}
              registry={registry}
              onChange={(t) => onChange({ type: t, _key: node._key })}
            />
            <Badge bg="light" text="primary" className="ms-2">{schema?.category || 'Predicate'}</Badge>
          </div>
          <div>
            <Button variant="light" className="text-primary me-1" size="sm" onClick={toGroup}>
              Wrap in group
            </Button>
            {removable && (
              <Button variant="light" className="text-danger" size="sm" onClick={onRemove}>
                <FontAwesomeIcon icon={faTrash} />
              </Button>
            )}
          </div>
        </div>
        {schema?.description && (
          <div className="text-muted small mb-2">{schema.description}</div>
        )}
        <LeafPredicateForm node={node} schema={schema} onChange={onChange} />
      </Card.Body>
    </Card>
  )
}

export default TreeNode
